/**
 * Signal Events service — the client surface of the unified signal log
 * (Signal Framework v2, SIG-0).
 *
 * Canon authority: docs/1-canon/23-signal-framework.md v2 §6.1.
 * Schema + RPCs: supabase/migrations/672_signal_events_unified.sql.
 * PRD: docs/9-exploration/SIGNAL-RESPONSE-V2-PRD.md.
 *
 * All writes go through the SECURITY DEFINER RPCs (Build Contract §2.3a) —
 * this module never inserts or updates signal rows directly. Reads go
 * through the typed client so contract drift is a compile error.
 *
 * Deliberate absences: nothing here sets or reads a condition letter, and
 * nothing gates a ceremony on linkage — an unlinked change simply wears its
 * derived exposure (canon 23 §6.3; exposure, not prevention).
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import { getAuthenticatedUserId } from './supabaseSync';
import type { Database } from '../types/database.generated';
import type {
  DecisionSignalExposureRow,
  SignalEventRow,
  SignalEventResponseRow,
  SignalEventStatus,
  SignalEventUnansweredRow,
  SignalExpectation,
  SignalResponseKind,
  SignalRoutingClass,
} from '../types/signal-events';

// ============================================================================
// RPC result shape
// ============================================================================

export interface SignalRpcResult {
  success: boolean;
  error?: string;
  signalEventId?: string;
  linkedCount?: number;
}

function parseRpcResult(data: unknown): SignalRpcResult {
  if (data === null || typeof data !== 'object') {
    return { success: false, error: 'Unexpected RPC result shape' };
  }
  const obj = data as Record<string, unknown>;
  return {
    success: obj.success === true,
    error: typeof obj.error === 'string' ? obj.error : undefined,
    signalEventId:
      typeof obj.signal_event_id === 'string' ? obj.signal_event_id : undefined,
    linkedCount:
      typeof obj.linked_count === 'number' ? obj.linked_count : undefined,
  };
}

async function requireActorId(): Promise<string | null> {
  return getAuthenticatedUserId();
}

// ============================================================================
// Capture
// ============================================================================

export interface RecordSignalEventInput {
  workspaceId: string;
  /** Chip id from SIGNAL_OCCURRENCES, or free text. */
  occurrence: string;
  message?: string;
  routingClass?: SignalRoutingClass;
  /** Only the honest capture states — the RPC rejects anything else. */
  status?: Extract<SignalEventStatus, 'monitoring' | 'confirmed'>;
  severity?: 'low' | 'medium' | 'high';
  /** ISO timestamp — the aging clock for signal_unanswered exposure. */
  reviewBy?: string;
  itemId?: string;
  sessionId?: string;
  cycleId?: string;
  porId?: string;
  /** Planning-family scope (682): the plan cycle this signal bears on. */
  planCycleId?: string;
  context?: Record<string, unknown>;
}

export async function recordSignalEvent(
  input: RecordSignalEventInput
): Promise<SignalRpcResult> {
  const actorId = await requireActorId();
  if (!actorId) return { success: false, error: 'Not authenticated' };

  const { data, error } = await getTypedSupabaseClient().rpc('record_signal_event', {
    p_workspace_id: input.workspaceId,
    p_actor_id: actorId,
    p_occurrence: input.occurrence,
    p_message: input.message,
    p_routing_class: input.routingClass,
    p_status: input.status ?? 'monitoring',
    p_severity: input.severity ?? 'medium',
    p_review_by: input.reviewBy,
    p_item_id: input.itemId,
    p_session_id: input.sessionId,
    p_cycle_id: input.cycleId,
    p_por_id: input.porId,
    p_context: (input.context ?? {}) as never,
    p_plan_cycle_id: input.planCycleId,
  });
  if (error) return { success: false, error: error.message };
  return parseRpcResult(data);
}

// ============================================================================
// Reading: classification and confirmation
// ============================================================================

/**
 * Assign the routing class — the human commit path. The AI routing advisor
 * (SIG-4) proposes into UI only and never calls this (P-AGENT-1).
 * Reclassifying an already-classified signal requires a rationale.
 */
export async function classifySignalEvent(
  signalEventId: string,
  routingClass: SignalRoutingClass,
  rationale?: string
): Promise<SignalRpcResult> {
  const actorId = await requireActorId();
  if (!actorId) return { success: false, error: 'Not authenticated' };

  const { data, error } = await getTypedSupabaseClient().rpc('classify_signal_event', {
    p_signal_id: signalEventId,
    p_actor_id: actorId,
    p_routing_class: routingClass,
    p_rationale: rationale,
  });
  if (error) return { success: false, error: error.message };
  return parseRpcResult(data);
}

export async function confirmSignalEvent(
  signalEventId: string,
  rationale?: string
): Promise<SignalRpcResult> {
  const actorId = await requireActorId();
  if (!actorId) return { success: false, error: 'Not authenticated' };

  const { data, error } = await getTypedSupabaseClient().rpc('confirm_signal_event', {
    p_signal_id: signalEventId,
    p_actor_id: actorId,
    p_rationale: rationale,
  });
  if (error) return { success: false, error: error.message };
  return parseRpcResult(data);
}

// ============================================================================
// Response linkage + pre-registration
// ============================================================================

/** Drop empty expectations before they reach the ceremony record. */
export function toExpectationsPayload(
  expectations: readonly SignalExpectation[] | undefined
): SignalExpectation[] {
  return (expectations ?? [])
    .map((e) => ({ ...e, expectation: e.expectation.trim() }))
    .filter((e) => e.expectation.length > 0);
}

/**
 * Link a ceremony Decision to the Signal Events it answers, and (for
 * change/hold) record pre-registered expectations into the Decision's
 * reconsideration_conditions + review_by_date. One transactional RPC.
 *
 * Every referenced signal must already carry a routing class — the RPC
 * rejects unclassified signals by name.
 */
export async function respondToSignals(
  decisionId: string,
  signalEventIds: readonly string[],
  responseKind: Exclude<SignalResponseKind, 'dismiss'>,
  opts?: {
    expectations?: readonly SignalExpectation[];
    /** ISO date (YYYY-MM-DD). */
    reviewByDate?: string;
  }
): Promise<SignalRpcResult> {
  const actorId = await requireActorId();
  if (!actorId) return { success: false, error: 'Not authenticated' };

  const expectations = toExpectationsPayload(opts?.expectations);
  const { data, error } = await getTypedSupabaseClient().rpc('respond_to_signals', {
    p_decision_id: decisionId,
    p_actor_id: actorId,
    p_signal_event_ids: [...signalEventIds],
    p_response_kind: responseKind,
    p_expectations: (expectations.length > 0 ? expectations : undefined) as never,
    p_review_by_date: opts?.reviewByDate,
  });
  if (error) return { success: false, error: error.message };
  return parseRpcResult(data);
}

/**
 * Answer a signal from its row: create the Decision the answer hangs on, then
 * link it through respond_to_signals in one flow. This is the loop closing —
 * "Hold course" / "Change course" become a Decision on the record with
 * pre-registered expectations (canon 23 §6.1).
 *
 * The Decision is written into the SIGNAL's workspace (never the session's —
 * record-scoped surfaces can be viewed from any workspace/demo state; same
 * defect class as the #458 gate fix). Mirrors the decisionsStore insert shape.
 */
export async function answerSignal(opts: {
  workspaceId: string;
  signalEventId: string;
  /** Decision title, e.g. `Held course against "…"`. Caller supplies the label. */
  decisionTitle: string;
  responseKind: 'change' | 'hold';
  /** Why — becomes the Decision body. Required: an answer without reasons is not an answer. */
  rationale: string;
  /** "If this holds, we expect …" — pre-registered so the future can judge. */
  expectation?: string;
  /** ISO date (YYYY-MM-DD) — when we said we'd check. */
  checkBy?: string;
  cycleId?: string | null;
  itemId?: string | null;
}): Promise<SignalRpcResult & { decisionId?: string }> {
  const actorId = await requireActorId();
  if (!actorId) return { success: false, error: 'Not authenticated' };
  if (!opts.rationale.trim()) {
    return { success: false, error: 'A rationale is required — it becomes the decision on the record.' };
  }

  // One governed write (record_decision, mig 765): the server stamps the
  // decider and records the governance event.
  const { data: recorded, error: recordError } = await getTypedSupabaseClient().rpc('record_decision', {
    p_workspace_id: opts.workspaceId,
    p_title: opts.decisionTitle,
    p_fields: {
      body: opts.rationale.trim(),
      source: 'signal_response',
      source_event_id: opts.signalEventId,
      cycle_id: opts.cycleId ?? null,
      item_id: opts.itemId ?? null,
      impact: 'medium',
    },
  });
  const recordedEnv = recorded as { success?: boolean; error?: string; decision?: { id?: string } } | null;
  const decisionId = recordedEnv?.decision?.id;
  if (recordError || !recordedEnv?.success || !decisionId) {
    const reason = recordError?.message ?? recordedEnv?.error ?? 'the server did not confirm it';
    return { success: false, error: `Could not record the decision: ${reason}` };
  }

  const result = await respondToSignals(decisionId, [opts.signalEventId], opts.responseKind, {
    expectations: opts.expectation?.trim()
      ? [{ expectation: opts.expectation.trim(), check_by: opts.checkBy }]
      : undefined,
    reviewByDate: opts.checkBy,
  });
  if (!result.success) {
    // Don't leave an orphan decision with no linked response on the record.
    await getTypedSupabaseClient().rpc('delete_decision', {
      p_decision_id: decisionId,
      p_reason: 'signal response could not be recorded',
    });
    return result;
  }
  // The caller carries the decision forward — a change-course answer flows
  // into the replan act with this decision attached (canon 23: the change
  // carries its why).
  return { ...result, decisionId };
}

/** Dismissal always requires a rationale — no silent disposal. */
export async function dismissSignalEvent(
  signalEventId: string,
  rationale: string,
  decisionId?: string
): Promise<SignalRpcResult> {
  const actorId = await requireActorId();
  if (!actorId) return { success: false, error: 'Not authenticated' };

  const { data, error } = await getTypedSupabaseClient().rpc('dismiss_signal_event', {
    p_signal_id: signalEventId,
    p_actor_id: actorId,
    p_rationale: rationale,
    p_decision_id: decisionId,
  });
  if (error) return { success: false, error: error.message };
  return parseRpcResult(data);
}

// ============================================================================
// Reads (RLS-scoped)
// ============================================================================

export interface ListSignalEventsOptions {
  workspaceId: string;
  status?: SignalEventStatus;
  porId?: string;
  itemId?: string;
  cycleId?: string;
  limit?: number;
}

export async function listSignalEvents(
  opts: ListSignalEventsOptions
): Promise<SignalEventRow[]> {
  let query = getTypedSupabaseClient()
    .from('signal_events')
    .select('*')
    .eq('workspace_id', opts.workspaceId)
    .order('created_at', { ascending: false })
    .limit(opts.limit ?? 100);

  if (opts.status) query = query.eq('status', opts.status);
  if (opts.porId) query = query.eq('por_id', opts.porId);
  if (opts.itemId) query = query.eq('item_id', opts.itemId);
  if (opts.cycleId) query = query.eq('cycle_id', opts.cycleId);

  const { data, error } = await query;
  if (error) throw new Error(`listSignalEvents failed: ${error.message}`);
  return data ?? [];
}

export async function listSignalResponsesForDecision(
  decisionId: string
): Promise<SignalEventResponseRow[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('signal_event_responses')
    .select('*')
    .eq('decision_id', decisionId)
    .order('created_at', { ascending: true });
  if (error) {
    throw new Error(`listSignalResponsesForDecision failed: ${error.message}`);
  }
  return data ?? [];
}

// ============================================================================
// Derived exposure (migration 673 read model — the ONLY truth source;
// UI must never recompute these predicates. Doc 07 posture.)
// ============================================================================

/** Confirmed signals past their review date with no referenced answer. */
export async function listUnansweredSignals(
  workspaceId: string,
  opts?: { porId?: string; cycleId?: string; limit?: number }
): Promise<SignalEventUnansweredRow[]> {
  let query = getTypedSupabaseClient()
    .from('signal_events_unanswered')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('review_by', { ascending: true })
    .limit(opts?.limit ?? 100);
  if (opts?.porId) query = query.eq('por_id', opts.porId);
  if (opts?.cycleId) query = query.eq('cycle_id', opts.cycleId);

  const { data, error } = await query;
  if (error) throw new Error(`listUnansweredSignals failed: ${error.message}`);
  return data ?? [];
}

export async function getDecisionSignalExposure(
  decisionId: string
): Promise<DecisionSignalExposureRow | null> {
  const { data, error } = await getTypedSupabaseClient()
    .from('decision_signal_exposure')
    .select('*')
    .eq('decision_id', decisionId)
    .maybeSingle();
  if (error) {
    throw new Error(`getDecisionSignalExposure failed: ${error.message}`);
  }
  return data;
}

// ============================================================================
// The traceability distribution (SIG-5) — the org-level credibility read.
// Computed from the record; requires no verdicts (canon 23 v2 §6.5).
// ============================================================================

export type SignalTraceabilityRow =
  Database['public']['Views']['signal_traceability_distribution']['Row'];

export async function getSignalTraceability(
  workspaceId: string,
  cycleId?: string | null
): Promise<SignalTraceabilityRow | null> {
  let query = getTypedSupabaseClient()
    .from('signal_traceability_distribution')
    .select('*')
    .eq('workspace_id', workspaceId);
  query = cycleId ? query.eq('cycle_id', cycleId) : query.is('cycle_id', null);

  const { data, error } = await query.maybeSingle();
  if (error) throw new Error(`getSignalTraceability failed: ${error.message}`);
  return data;
}

// ============================================================================
// The amendment record (SIG-3) — responses on the record, readable by
// someone who wasn't in the room: when, what signal, what response, what
// we said we'd expect, and when we said we'd check.
// ============================================================================

export interface SignalResponseRecordEntry {
  linkedAt: string;
  responseKind: SignalResponseKind;
  signalEventId: string;
  occurrence: string;
  decisionId: string;
  decisionTitle: string;
  reconsiderationConditions: string | null;
  reviewByDate: string | null;
}

interface SignalResponseRecordRow {
  created_at: string;
  response_kind: string;
  signal_events: { id: string; occurrence: string; por_id: string | null } | null;
  decisions: {
    id: string;
    title: string;
    reconsideration_conditions: string | null;
    review_by_date: string | null;
  } | null;
}

export async function listSignalResponseRecord(opts: {
  workspaceId: string;
  porId?: string;
  limit?: number;
}): Promise<SignalResponseRecordEntry[]> {
  let query = getTypedSupabaseClient()
    .from('signal_event_responses')
    .select(
      'created_at, response_kind, signal_events!inner(id, occurrence, por_id), decisions(id, title, reconsideration_conditions, review_by_date)'
    )
    .eq('workspace_id', opts.workspaceId)
    .order('created_at', { ascending: false })
    .limit(opts.limit ?? 50);
  if (opts.porId) query = query.eq('signal_events.por_id', opts.porId);

  const { data, error } = await query;
  if (error) throw new Error(`listSignalResponseRecord failed: ${error.message}`);

  return ((data ?? []) as unknown as SignalResponseRecordRow[])
    .filter((row) => row.signal_events !== null && row.decisions !== null)
    .map((row) => ({
      linkedAt: row.created_at,
      responseKind: row.response_kind as SignalResponseKind,
      signalEventId: row.signal_events!.id,
      occurrence: row.signal_events!.occurrence,
      decisionId: row.decisions!.id,
      decisionTitle: row.decisions!.title,
      reconsiderationConditions: row.decisions!.reconsideration_conditions,
      reviewByDate: row.decisions!.review_by_date,
    }));
}

// ============================================================================
// Terms on the commitment — the active decisions carrying pre-registered
// expectations ("if this holds, we expect Y — check by Z") for a plan's
// items. Rendered as the terms sub-line on ranked commitment rows: the
// conditions under which each commitment currently stands.
// ============================================================================

export interface TermExpectation {
  text: string;
  /** YYYY-MM-DD parsed from the "(check by …)" suffix respond_to_signals writes. */
  checkBy: string | null;
}

export interface ItemTermEntry {
  decisionId: string;
  itemId: string;
  decisionTitle: string;
  expectations: TermExpectation[];
  reviewByDate: string | null;
  decidedAt: string;
}

/** Pull the "- …" expectation bullets out of the recorded conditions text. */
export function extractExpectations(reconsiderationConditions: string | null): TermExpectation[] {
  if (!reconsiderationConditions) return [];
  return reconsiderationConditions
    .split('\n')
    .filter((line) => line.startsWith('- '))
    .map((line) => line.slice(2).trim())
    .filter(Boolean)
    .map((bullet) => {
      const m = bullet.match(/^(.*?)\s*\(check by (\d{4}-\d{2}-\d{2})\)$/);
      return m ? { text: m[1], checkBy: m[2] } : { text: bullet, checkBy: null };
    });
}

export async function listItemTerms(
  workspaceId: string,
  itemIds: string[]
): Promise<Record<string, ItemTermEntry[]>> {
  if (itemIds.length === 0) return {};
  const { data, error } = await getTypedSupabaseClient()
    .from('decisions')
    .select('id, item_id, title, reconsideration_conditions, review_by_date, decided_at')
    .eq('workspace_id', workspaceId)
    .eq('status', 'active')
    .in('item_id', itemIds)
    .or('reconsideration_conditions.not.is.null,review_by_date.not.is.null')
    .order('decided_at', { ascending: false });
  if (error) throw new Error(`listItemTerms failed: ${error.message}`);

  const out: Record<string, ItemTermEntry[]> = {};
  for (const row of data ?? []) {
    if (!row.item_id) continue;
    (out[row.item_id] ??= []).push({
      decisionId: row.id,
      itemId: row.item_id,
      decisionTitle: row.title,
      expectations: extractExpectations(row.reconsideration_conditions),
      reviewByDate: row.review_by_date,
      decidedAt: row.decided_at,
    });
  }
  return out;
}

export async function listDecisionSignalExposure(
  workspaceId: string,
  opts?: { cycleId?: string; limit?: number }
): Promise<DecisionSignalExposureRow[]> {
  let query = getTypedSupabaseClient()
    .from('decision_signal_exposure')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('decision_created_at', { ascending: false })
    .limit(opts?.limit ?? 100);
  if (opts?.cycleId) query = query.eq('cycle_id', opts.cycleId);

  const { data, error } = await query;
  if (error) {
    throw new Error(`listDecisionSignalExposure failed: ${error.message}`);
  }
  return data ?? [];
}
// ============================================================================
// Verdicts (canon 23 §4.2, mig 682) — reflection-time judgment, recorded once
// ============================================================================

export type SignalVerdict = 'held' | 'broke' | 'unobservable';

/**
 * Record whether what the signal warned about held, broke, or turned out
 * unobservable. The RPC enforces: once only, never on dismissed signals,
 * basis required, plan-governance authority.
 */
export async function recordSignalVerdict(
  signalId: string,
  verdict: SignalVerdict,
  basis: string
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await getTypedSupabaseClient().rpc('record_signal_verdict', {
    p_signal_id: signalId,
    p_verdict: verdict,
    p_basis: basis,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

// ============================================================================
// Tripped-term → signal linkage (break 3 of the decision→…→reprioritization
// chain audit; mig 682's trip_commitment_term best-effort insert)
// ============================================================================

/**
 * trip_commitment_term (mig 682) enters the unified signal log as a
 * best-effort side write — it returns the term id, not the new signal's id,
 * and the insert can silently fail (the RPC never blocks the trip on it).
 * So the link back from a tripped term to its signal is found, not carried:
 * this reads every 'commitment_term_tripped' signal on the plan cycle and
 * keys it by the term id it names in its context payload.
 *
 * One query for a whole plan cycle's worth of terms — callers should call
 * this once per cycle, not once per row, to avoid an N+1 fetch.
 */
export async function listTrippedTermSignals(
  planCycleId: string
): Promise<Map<string, SignalEventRow>> {
  const { data, error } = await getTypedSupabaseClient()
    .from('signal_events')
    .select('*')
    .eq('plan_cycle_id', planCycleId)
    .eq('occurrence', 'commitment_term_tripped')
    .order('created_at', { ascending: false });
  if (error || !data) return new Map();

  const byTermId = new Map<string, SignalEventRow>();
  for (const row of data) {
    const context = row.context as { term_id?: unknown } | null;
    const termId = context && typeof context.term_id === 'string' ? context.term_id : null;
    // Rows arrive newest-first, so the first one seen per term wins.
    if (termId && !byTermId.has(termId)) byTermId.set(termId, row);
  }
  return byTermId;
}
